import { Box } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  gridClasses,
  GridNoRowsOverlay,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid";
import { ScopeType } from "./hooks/useEntityScopes";

export interface ScopeTableProps<T extends { id: string }> {
  scopeType?: ScopeType;
  columns: GridColDef<T>[];
  data: T[];
  onScopeRowClick?: (rowId: string) => void;
  loading?: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  rowCount: number;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
}

export function ScopeTable<T extends { id: string }>({
  scopeType = "Organizations",
  columns,
  data,
  onScopeRowClick,
  loading = false,
  paginationModel,
  onPaginationModelChange,
  rowCount,
  sortModel,
  onSortModelChange,
}: ScopeTableProps<T>) {
  const isRowClickable = scopeType !== "Workspaces";

  const handleRowClick = (params: any) => {
    if (isRowClickable && onScopeRowClick) {
      onScopeRowClick(params.row.id);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <DataGrid
        rows={data}
        columns={columns}
        columnHeaderHeight={54}
        rowHeight={54}
        disableRowSelectionOnClick
        disableColumnMenu
        onRowClick={handleRowClick}
        loading={loading}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        rowCount={rowCount}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        slots={{ noRowsOverlay: NoRowsOverlay }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "skeleton",
          },
        }}
        sx={{
          borderRadius: 2,
          border: "none",
          backgroundColor: "background.paper",
          [`& .${gridClasses.columnSeparator}`]: {
            [`&:not(.${gridClasses["columnSeparator--resizable"]})`]: {
              display: "none",
            },
          },
          [`& .${gridClasses.columnHeader}`]: {
            ...tableHeaderStyles,
            backgroundColor: (theme) =>
              theme.palette.mode === "light" ? "#D9D9DA" : "background.dark",
            borderBottom: "none !important",
          },
          [`& .${gridClasses.sortIcon}`]: {
            fontSize: "10px",
            color: "text.primary",
          },
          [`& .${gridClasses.footerContainer}`]: {
            backgroundColor: (theme) =>
              theme.palette.mode === "light" ? "#D9D9DA" : "background.dark",
            borderTop: "none !important",
            backgroundImage: "none",
            opacity: 1,
          },
          [`& .${gridClasses.columnHeader}:focus`]: {
            outline: "none",
          },
          [`& .${gridClasses.columnHeader}:focus-within`]: {
            outline: "none",
          },
          [`& .${gridClasses.cell}:focus`]: {
            outline: "none",
          },
          [`& .${gridClasses.cell}:focus-within`]: {
            outline: "none",
          },
          "& .MuiDataGrid-row--borderBottom": {
            borderBottom: "none",
          },
          "& .left-cell": {
            pl: 3,
          },
          "& .right-cell": {
            pr: 3,
          },
          ...(isRowClickable && {
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
          }),
        }}
        pageSizeOptions={[10, 25, 50]}
      />
    </Box>
  );
}

const tableHeaderStyles = {
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "normal",
  color: "text.primary",
};

function NoRowsOverlay() {
  return (
    <GridNoRowsOverlay
      sx={{
        backgroundColor: "background.paper",
      }}
    />
  );
}
